import React, { useState, useCallback, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import Button from '../shared/Button'
import ResetButton from '../shared/ResetButton'
import SearchableDropdown from '../shared/SearchableDropdown'
import ConfirmationDialog from '../shared/ConfirmationDialog'
import API from '../../API/API'
import { useAlerts } from '../../contexts/AlertContext'
import logger from '../../utils/logger'
import { ConvertibleValidation } from '../../types/helmetLookup'
import { 
  handleOrganizationSelection,
  createOrgTeamDisplayName
} from '../../utils/shared/validation'

// Define form interfaces
interface MoveHelmetOrgFormData {
  sourceOrgID: string  // Form values are strings, converted to numbers for processing
  sourceTeamID: string  // Source team selection when multiple teams exist
  helmetID: string
  targetOrgID: string  // Form values are strings, converted to numbers for processing
  targetTeamID: string  // Team selection for target organization
  confirmTransfer: boolean
}

interface HelmetInfo {
  playerUnitSerialString: string
  sourceOrgID: number
  sourceOrgName: string
  currentPlayer?: {
    playerName: string
    playerNumber: string
  }
  helmetRFIDLabel?: string
  rfidBarcode?: string
  jerseyNumber?: number
  // Extended fields for helmet update
  originalMaterialNo?: string
  helmetHatSize?: string
  features?: string | null
  shell?: string
  shellColor?: string
  colorCode?: string
  paintCode1?: string
  paintCode1Description?: string
  paintCode2?: string
  paintCode2Description?: string
  helmetType?: string
  helmetColor?: string
  paintDesignCode?: string
  paintDesignDescription?: string
  helmet_config_id?: any
}

interface Organization {
  orgID: number  // Only use orgID as number from API
  orgName: string
  orgNameShort?: string
  orgCity?: string
  orgZip?: string
  orgAddress1?: string
  orgStateID?: number
}

interface Team {
  teamID: number
  teamName: string
  orgID: number
  orgName?: string
}

interface OrgHelmet {
  // Helmet identification
  helmetID: string

  helmetRFIDLabel?: string
  rfidBarcode?: string
  
  // Player Unit information
  playerUnitSerialString: string
  playerUnitID?: number
  playerUnitFirmwareVersionSemVer?: string
  playerUnitHardwareNumber?: string
  playerUnitLastModifiedDate?: string

  // Player information
  playerID?: number
  firstName?: string
  lastName?: string
  playerName?: string  // Computed from firstName + lastName
  jerseyNumber?: string
  middleInitial?: string
  
  // Team/Organization information
  teamID?: number
  teamName?: string
  orgID?: number
  orgName?: string
  
  // Equipment/Assignment information
  playersEquipmentID?: string
  helmetPlayerUnitID?: number
  
  // Additional fields that might be in API response
  levelsClassID?: number
  positionID?: number

  // Extended helmet information fields
  originalMaterialNo?: string
  helmetHatSize?: string
  features?: string | null
  shell?: string
  shellColor?: string
  colorCode?: string
  paintCode1?: string
  paintCode1Description?: string
  paintCode2?: string
  paintCode2Description?: string
  helmetType?: string
  helmetColor?: string
  paintDesignCode?: string
  paintDesignDescription?: string
  helmet_config_id?: any
}

const extractApiErrorDetail = (error: any): string => {
  const responseData = error?.response?.data

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData
  }

  if (responseData?.message && typeof responseData.message === 'string') {
    return responseData.message
  }

  if (responseData?.error && typeof responseData.error === 'string') {
    return responseData.error
  }

  if (responseData?.result && typeof responseData.result === 'string') {
    return responseData.result
  }

  return ''
}

const MoveHelmetOrgFlow: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false)
  const [isLoadingHelmets, setIsLoadingHelmets] = useState(false)
  const [isLoadingTargetTeam, setIsLoadingTargetTeam] = useState(false)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [selectedSourceOrg, setSelectedSourceOrg] = useState<Organization | null>(null)
  const [selectedSourceTeam, setSelectedSourceTeam] = useState<Team | null>(null)
  const [sourceTeams, setSourceTeams] = useState<Team[]>([])
  const [sourceTeamLoadError, setSourceTeamLoadError] = useState<string | null>(null)
  const [SourceTeamHelmets, setSourceTeamHelmets] = useState<OrgHelmet[]>([])
  const [targetTeamLoadError, setTargetTeamLoadError] = useState<string | null>(null)
  const [availableTargetTeams, setAvailableTargetTeams] = useState<any[]>([])
  const [selectedTargetTeam, setSelectedTargetTeam] = useState<any>(null)
  const [selectedHelmet, setSelectedHelmet] = useState<OrgHelmet | null>(null)
  const [helmetInfo, setHelmetInfo] = useState<HelmetInfo | null>(null)

  // Convertible helmet validation state
  const [convertibleValidation, setConvertibleValidation] = useState<ConvertibleValidation | null>(null)
  const [isValidatingConvertible, setIsValidatingConvertible] = useState(false)
  const [showConvertibleModal, setShowConvertibleModal] = useState(false)

  // Ref to prevent concurrent helmet loading for the same team
  const currentLoadingTeamRef = useRef<number | null>(null)

  // Transfer success state with meaningful information
  const [transferSuccess, setTransferSuccess] = useState<{
    helmetDisplay: string
    sourceDisplay: string
    targetDisplay: string
  } | null>(null)

  const { addAlert } = useAlerts()
  const { register, handleSubmit, formState: { errors }, watch, setValue, trigger } = useForm<MoveHelmetOrgFormData>({    defaultValues: {
      sourceOrgID: '',
      sourceTeamID: '',
      helmetID: '',
      targetOrgID: '',
      targetTeamID: '',
      confirmTransfer: false
    }
  })

  // Register the sourceOrgID field with validation
  React.useEffect(() => {
    register('sourceOrgID', { required: 'Source organization is required' })
    register('targetOrgID', { required: 'Target organization is required' })
  }, [register])
  
  const watchedSourceOrgID = watch('sourceOrgID')
  const watchedSourceTeamID = watch('sourceTeamID')
  const watchedHelmetID = watch('helmetID')
  const watchedTargetOrgID = watch('targetOrgID')
  const watchedTargetTeamID = watch('targetTeamID')
  // Helper function to get organization ID - now returns number
  const getOrgID = (org: Organization): number => {
    return org.orgID
  }

  // Helper function to check if error is connection refused
  const isConnectionRefused = (error: any): boolean => {
    return error.code === 'ERR_CONNECTION_REFUSED' || 
           error.message?.includes('ERR_CONNECTION_REFUSED') ||
           error.message?.includes('Network Error') ||
           error.message?.includes('connect ECONNREFUSED')
  }

  // Helper function to handle API errors with connection check
  const handleApiError = useCallback((error: any, defaultMessage: string) => {
    if (isConnectionRefused(error)) {
      addAlert('error', 'The Insite Server cannot be reached')
    } else {
      const errorMessage = error.response?.data?.message || error.message || defaultMessage
      addAlert('error', errorMessage)
    }
  }, [addAlert])

  const loadOrganizations = useCallback(async () => {
    try {
      setIsLoadingOrgs(true)
      const response = await API.GetOrgs()
      if (response.success && response.data) {
        setOrganizations(response.data)
      }
    } catch (error: any) {
      logger.error('Failed to load organizations', { module: 'MoveHelmetOrgFlow', data: { error: error.message } })
      handleApiError(error, 'Failed to load organizations')
    } finally {
      setIsLoadingOrgs(false)
    }
  }, [handleApiError])

  const loadTeamHelmets = useCallback(async (teamId: number) => {
    // Prevent concurrent calls for the same team
    if (currentLoadingTeamRef.current === teamId) {
      return
    }
    
    currentLoadingTeamRef.current = teamId
    
    try {
      setIsLoadingHelmets(true)
      
      // Require teamId to be provided - always use teamID for GetHelmets
      if (!teamId) {
        throw new Error('Team ID is required to load helmets');
      }
      
      // Use real API call to get helmets for the team
      const response = await API.GetHelmets(teamId);
      
      if (response.success && response.data && response.data.length > 0) {
        // Map API response to component data structure - preserve all fields
        const helmets: OrgHelmet[] = response.data.map((item: any) => {
          const helmetID = String(item.helmetID || '')
          return {
            // Helmet identification
            helmetID,
            helmetRFIDLabel: item.helmetRFIDLabel || '',
            rfidBarcode: item.rfidBarcode || '',
            
            // Player Unit information - preserve all original fields
            playerUnitSerialString: item.playerUnitSerialString || '',
            playerUnitID: item.playerUnitID || undefined,
            playerUnitFirmwareVersionSemVer: item.playerUnitFirmwareVersionSemVer || undefined, 
            playerUnitHardwareNumber: item.playerUnitHardwareNumber || undefined,
            playerUnitLastModifiedDate: item.playerUnitLastModifiedDate || undefined,

            // Player information - preserve all original fields
            playerID: item.playerID || undefined,
            firstName: item.firstName || undefined,
            lastName: item.lastName || undefined,
            playerName: item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : undefined,
            jerseyNumber: String(item.jerseyNumber || ''),
            middleInitial: item.middleInitial || undefined,
            
            // Team/Organization information
            teamID: item.teamID || undefined,
            teamName: item.teamName || undefined,
            orgID: item.orgID || undefined,
            orgName: item.orgName || undefined,
            
            // Equipment/Assignment information
            playersEquipmentID: item.playersEquipmentID || undefined,
            helmetPlayerUnitID: item.helmetPlayerUnitID || undefined,
            
            // Additional fields
            levelsClassID: item.levelsClassID || undefined,
            positionID: item.positionID || undefined,
            
            // Extended helmet information fields
            originalMaterialNo: item.originalMaterialNo || undefined,
            helmetHatSize: item.helmetHatSize || undefined,
            features: item.features ?? undefined,
            shell: item.shell || undefined,
            shellColor: item.shellColor || undefined,
            colorCode: item.colorCode || undefined,
            paintCode1: item.paintCode1 || undefined,
            paintCode1Description: item.paintCode1Description || undefined,
            paintCode2: item.paintCode2 || undefined,
            paintCode2Description: item.paintCode2Description || undefined,
            helmetType: item.helmetType || undefined,
            helmetColor: item.helmetColor || undefined,
            paintDesignCode: item.paintDesignCode || undefined,
            paintDesignDescription: item.paintDesignDescription || undefined,
            helmet_config_id: item.helmet_config_id ?? undefined,
          }
        })
        
        setSourceTeamHelmets(helmets)
        currentLoadingTeamRef.current = null // Clear loading ref on success
      } else {
        // No helmets found - either empty data array or unsuccessful response
        setSourceTeamHelmets([])
        // Avoid spamming the global toast area when teams have no helmets.
        // Log for diagnostics and show inline messaging in the UI instead.
        logger.info('No helmets found for team', { module: 'MoveHelmetOrgFlow', data: { teamId } })
        currentLoadingTeamRef.current = null // Clear loading ref
      }
    } catch (error: any) {
      logger.error('Failed to load team helmets', { 
        module: 'MoveHelmetOrgFlow', 
        data: { error: error.message, teamId, } 
      })
      handleApiError(error, teamId ? 'Failed to load helmets for selected team' : 'Failed to load helmets for selected team')
      setSourceTeamHelmets([])
      currentLoadingTeamRef.current = null // Clear loading ref on error
      // Don't add an additional alert here since handleApiError already shows an error alert
    } finally {
      setIsLoadingHelmets(false)
    }
  }, [handleApiError, addAlert])

  const loadTargetTeam = useCallback(async (orgId: string) => {
    try {
      setIsLoadingTargetTeam(true)
      
      // Get teams for the target organization
      const response = await API.GetOrgTeams(orgId)
      
      if (response.success && response.data && response.data.length > 0) {
        setAvailableTargetTeams(response.data)
        setTargetTeamLoadError(null)
        
        if (response.data.length === 1) {
          // If only one team, auto-select it
          setSelectedTargetTeam(response.data[0])
          setValue('targetTeamID', response.data[0].teamID.toString())
        } else {
          // Multiple teams available, let user choose
          setSelectedTargetTeam(null)
          setValue('targetTeamID', '')
        }
      } else {
        setAvailableTargetTeams([])
        setSelectedTargetTeam(null)
        setValue('targetTeamID', '')
        // Show inline message instead of a global toast to avoid spamming
        setTargetTeamLoadError('No team for selected organization')
        logger.info('No teams found for selected target organization', { module: 'MoveHelmetOrgFlow', data: { orgId } })
      }
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : String(error)
      const isNoTeams = typeof msg === 'string' && msg.toLowerCase().includes('no teams found for organization')
      logger.error('Failed to load target team', { module: 'MoveHelmetOrgFlow', data: { error: error.message, orgId } })
      if (isNoTeams) {
        // avoid toast; show inline error and clear team list
        setTargetTeamLoadError('No team under organization. Select different organization')
        setAvailableTargetTeams([])
        setSelectedTargetTeam(null)
        setValue('targetTeamID', '')
      } else {
        handleApiError(error, 'Failed to load team information for target organization')
        setAvailableTargetTeams([])
        setSelectedTargetTeam(null)
        setValue('targetTeamID', '')
      }
    } finally {
      setIsLoadingTargetTeam(false)
    }
  }, [handleApiError, setValue])

  // Load organizations on component mount
  React.useEffect(() => {
    loadOrganizations()
   }, []) // Only run once on mount
  
  // Handle source team selection
  React.useEffect(() => {
    if (watchedSourceTeamID && selectedSourceOrg) {
      const teamId = parseInt(watchedSourceTeamID)
      const team = sourceTeams.find(t => t.teamID === teamId)
      if (team && team.teamID !== selectedSourceTeam?.teamID) {
        setSelectedSourceTeam(team)
        // Load helmets for the selected team
        loadTeamHelmets(team.teamID)
      }
    } else if (!watchedSourceTeamID) {
      // Clear helmets when no team is selected
      setSourceTeamHelmets([])
      setSelectedHelmet(null)
      setHelmetInfo(null)
      setValue('helmetID', '')
    }
  }, [watchedSourceTeamID, sourceTeams, selectedSourceTeam?.teamID, selectedSourceOrg, loadTeamHelmets, setValue])
  
  // Load helmets when source organization changes
  React.useEffect(() => {
    if (watchedSourceOrgID && organizations.length > 0) {
      const sourceOrgIDNum = Number(watchedSourceOrgID)
      const selectedOrg = organizations.find(org => getOrgID(org) === sourceOrgIDNum)
      
      if (selectedOrg) {
        // Use the same organization selection logic as MPH
        handleOrganizationSelection(selectedOrg)
          .then(result => {
            setSelectedSourceOrg(selectedOrg)
            setSourceTeams(result.teams)
            setSourceTeamLoadError(null)
            
            if (!result.requiresTeamSelection && result.team) {
              // Auto-select the single team
              setSelectedSourceTeam(result.team)
              setValue('sourceTeamID', result.team.teamID.toString())
              // Load helmets for the specific team
              loadTeamHelmets(result.team.teamID)
            } else {
              // Multiple teams - user needs to select
              setSelectedSourceTeam(null)
              setValue('sourceTeamID', '')
              // Clear helmets until team is selected
              setSourceTeamHelmets([])
            }
          })
          .catch(error => {
            const msg = error instanceof Error ? error.message : String(error)
            const isNoTeams = typeof msg === 'string' && msg.toLowerCase().includes('no teams found for organization')
            if (isNoTeams) {
              setSourceTeamLoadError('No team for selected organization')
              setSelectedSourceOrg(null)
              setSourceTeams([])
              setSelectedSourceTeam(null)
              logger.info('No teams found for selected source organization', { module: 'MoveHelmetOrgFlow', data: { orgID: selectedOrg.orgID } })
            } else {
              logger.error('Failed to load teams for organization', { module: 'MoveHelmetOrgFlow', data: { error: (error as Error).message } })
              addAlert('error', 'Failed to load teams for organization')
              setSelectedSourceOrg(null)
              setSourceTeams([])
              setSelectedSourceTeam(null)
            }
          })
      } else {
        setSelectedSourceOrg(null)
        setSourceTeams([])
        setSelectedSourceTeam(null)
      }
      
      // Clear helmet selection when org changes
      setSelectedHelmet(null)
      setHelmetInfo(null)
      setTransferSuccess(null)
      setValue('helmetID', '')
      setValue('sourceTeamID', '')
      setValue('confirmTransfer', false)
      
      // Clear target org and team when source org changes
      setValue('targetOrgID', '')
      setValue('targetTeamID', '')
      setAvailableTargetTeams([])
      setSelectedTargetTeam(null)
      setTargetTeamLoadError(null)
    } else {
      setSelectedSourceOrg(null)
      setSourceTeams([])
      setSelectedSourceTeam(null)
      setSourceTeamHelmets([])
      setSelectedHelmet(null)
      setHelmetInfo(null)
      setTransferSuccess(null)
      setValue('sourceTeamID', '')
      setValue('helmetID', '')
      
      // Clear target org and team when source org is cleared
      setValue('targetOrgID', '')
      setValue('targetTeamID', '')
      setAvailableTargetTeams([])
      setSelectedTargetTeam(null)
      setTargetTeamLoadError(null)
      setValue('confirmTransfer', false)
    }
  }, [watchedSourceOrgID, organizations, setValue, loadTeamHelmets, handleApiError])
  
  // Load helmets when source team is selected (either auto-selected or manually selected)
  React.useEffect(() => {
    if (selectedSourceTeam) {
      // Load helmets for the selected team
      loadTeamHelmets(selectedSourceTeam.teamID)
      
      // Clear any previous helmet selection
      setSelectedHelmet(null)
      setHelmetInfo(null)
      setTransferSuccess(null)
      setValue('helmetID', '')
      setValue('confirmTransfer', false)
      
      // Clear target org and team when source team changes
      setValue('targetOrgID', '')
      setValue('targetTeamID', '')
      setAvailableTargetTeams([])
      setSelectedTargetTeam(null)
      setTargetTeamLoadError(null)
    } else {
      // Clear helmet data when no team is selected
      setSourceTeamHelmets([])
      setSelectedHelmet(null)
      setHelmetInfo(null)
      setTransferSuccess(null)
      setValue('helmetID', '')
      
      // Clear target org and team when source team is cleared
      setValue('targetOrgID', '')
      setValue('targetTeamID', '')
      setAvailableTargetTeams([])
      setSelectedTargetTeam(null)
      setTargetTeamLoadError(null)
      setValue('confirmTransfer', false)
    }
  }, [selectedSourceTeam, setValue, loadTeamHelmets])
  
  // Load helmet info when helmet is selected
  React.useEffect(() => {
    if (watchedHelmetID && selectedSourceOrg && SourceTeamHelmets.length > 0) {
      const selectedHelmetData = SourceTeamHelmets.find((helmet, index) => {
        const helmetID = helmet.rfidBarcode || `helmet-${index}`
        return helmetID === watchedHelmetID
      })
      
      setSelectedHelmet(selectedHelmetData || null)
      
      if (selectedHelmetData) {
        // Create helmet info directly from the selected helmet data - with all extended fields
        const helmetInfo: HelmetInfo = {
          playerUnitSerialString: selectedHelmetData.playerUnitSerialString,
          sourceOrgID: getOrgID(selectedSourceOrg),
          sourceOrgName: selectedSourceOrg.orgName,
          jerseyNumber: Number(selectedHelmetData.jerseyNumber) || undefined,
          currentPlayer: selectedHelmetData.playerName ? {
            playerName: selectedHelmetData.playerName,
            playerNumber: selectedHelmetData.jerseyNumber || ''
          } : undefined,
          helmetRFIDLabel: selectedHelmetData.helmetRFIDLabel,
          rfidBarcode: selectedHelmetData.rfidBarcode,
          // Extended fields
          originalMaterialNo: selectedHelmetData.originalMaterialNo,
          helmetHatSize: selectedHelmetData.helmetHatSize,
          features: selectedHelmetData.features,
          shell: selectedHelmetData.shell,
          shellColor: selectedHelmetData.shellColor,
          colorCode: selectedHelmetData.colorCode,
          paintCode1: selectedHelmetData.paintCode1,
          paintCode1Description: selectedHelmetData.paintCode1Description,
          paintCode2: selectedHelmetData.paintCode2,
          paintCode2Description: selectedHelmetData.paintCode2Description,
          helmetType: selectedHelmetData.helmetType,
          helmetColor: selectedHelmetData.helmetColor,
          paintDesignCode: selectedHelmetData.paintDesignCode,
          paintDesignDescription: selectedHelmetData.paintDesignDescription,
          helmet_config_id: selectedHelmetData.helmet_config_id
        }
        setHelmetInfo(helmetInfo)
      } else {
        setHelmetInfo(null)
      }
      
      // Always clear target org and team when helmet selection changes
      setValue('targetOrgID', '')
      setValue('targetTeamID', '')
      setValue('confirmTransfer', false)
      setAvailableTargetTeams([])
      setSelectedTargetTeam(null)
      setTargetTeamLoadError(null)
      setConvertibleValidation(null)
    } else {
      // Clear when helmet is not selected or no data is available
      setSelectedHelmet(null)
      setHelmetInfo(null)
      setConvertibleValidation(null)
      
      // Always clear target org and team when helmet is cleared
      setValue('targetOrgID', '')
      setValue('targetTeamID', '')
      setValue('confirmTransfer', false)
      setAvailableTargetTeams([])
      setSelectedTargetTeam(null)
      setTargetTeamLoadError(null)
    }
  }, [watchedHelmetID, selectedSourceOrg, SourceTeamHelmets, setValue])

  // Load target team when target organization changes
  React.useEffect(() => {
    if (watchedTargetOrgID && organizations.length > 0) {
      const targetOrgIDNum = Number(watchedTargetOrgID)
      loadTargetTeam(String(targetOrgIDNum))
    } else {
      setAvailableTargetTeams([])
      setSelectedTargetTeam(null)
      setValue('targetTeamID', '')
      setTargetTeamLoadError(null)
    }
    setValue('confirmTransfer', false)
    setConvertibleValidation(null)
  }, [watchedTargetOrgID, organizations, loadTargetTeam, setValue])

  // Update selected team when team ID changes
  React.useEffect(() => {
    if (watchedTargetTeamID && availableTargetTeams.length > 0) {
      const selectedTeam = availableTargetTeams.find(team => 
        team.teamID.toString() === watchedTargetTeamID
      )
      setSelectedTargetTeam(selectedTeam || null)
    } else {
      setSelectedTargetTeam(null)
    }
  }, [watchedTargetTeamID, availableTargetTeams])

  // Validate convertible helmets when helmet and target team are both selected
  React.useEffect(() => {
    const validateConvertibleHelmet = async () => {
      if (selectedHelmet?.features?.toLowerCase() !== 'convertible' || !selectedTargetTeam) {
        setConvertibleValidation(null)
        return
      }

      try {
        setIsValidatingConvertible(true)
        
        // Call GetRoster to get all players in target team
        const rosterResponse = await API.GetRoster(selectedTargetTeam.teamID)
        const players = rosterResponse.data || []
        
        // Search for player with name "RIDDELL {helmetHatSize}"
        const matchingPlayer = players.find((player: any) => 
          player.firstName?.toUpperCase() === 'RIDDELL' &&
          player.lastName?.toUpperCase() === (selectedHelmet.helmetHatSize || '').toUpperCase()
        )
        
        if (matchingPlayer) {
          setConvertibleValidation({
            isConvertible: true,
            requiredPlayer: {
              playerID: matchingPlayer.playerID,
              playerName: `${matchingPlayer.firstName} ${matchingPlayer.lastName}`,
              firstName: matchingPlayer.firstName,
              lastName: matchingPlayer.lastName,
              jerseyNumber: matchingPlayer.jerseyNumber,
              positionID: matchingPlayer.positionID,
              playerUnitID: matchingPlayer.playerUnitID
            },
            isValid: true,
            errorMessage: null
          })
        } else {
          setConvertibleValidation({
            isConvertible: true,
            requiredPlayer: null,
            isValid: false,
            errorMessage: `This is a Convertible helmet. The target organization must have a player named "RIDDELL ${selectedHelmet.helmetHatSize}" to receive this helmet.`
          })
        }
      } catch (error: any) {
        logger.error('Failed to validate convertible helmet', { error, helmetInfo })
        setConvertibleValidation({
          isConvertible: true,
          requiredPlayer: null,
          isValid: false,
          errorMessage: 'Failed to validate convertible helmet requirements. Please try again.'
        })
      } finally {
        setIsValidatingConvertible(false)
        // Show modal after validation completes
        setShowConvertibleModal(true)
      }
    }

    validateConvertibleHelmet()
  }, [selectedHelmet, selectedTargetTeam])

  const transferPlayerUnitToTeam = useCallback(async (
    serial: string,
    firmwareVersion: string,
    hardwareNumber: number | string | undefined,
    teamID: number
  ) => {
    const normalizedHardwareNumber = Number(hardwareNumber)
    const normalizedTeamID = Number(teamID)
    const safeHardwareNumber = Number.isFinite(normalizedHardwareNumber)
      ? normalizedHardwareNumber
      : 0

    if (!serial || !Number.isFinite(normalizedTeamID)) {
      throw new Error('Invalid player unit transfer parameters')
    }

    const requiredPayload = {
      playerUnitSerialString: serial,
      playerUnitFirmwareVersion: firmwareVersion || '',
      playerUnitHardwareNumber: safeHardwareNumber,
      playerUnitAddress: 0,
      slot: 0,
      superFrame: 0,
      teamID: normalizedTeamID,
    }

    return await API.PostPlayerUnitToTeam(requiredPayload)
  }, [])
  
  const onSubmit = async (data: MoveHelmetOrgFormData) => {
    if (!helmetInfo || !selectedHelmet) {
      addAlert('error', 'Helmet information not loaded')
      return
    }

    // Validate source team selection if multiple teams exist
    if (sourceTeams.length > 1 && !selectedSourceTeam) {
      addAlert('error', 'Please select a source team')
      return
    }

    // Validate that we have all required data
    if (!validateTransferData(selectedHelmet, selectedTargetOrg)) {
      return
    }
    
    try {
      setIsSubmitting(true)
      
      // Save the original playerUnitSerialString before transfer
      const originalPlayerUnitSerialString = selectedHelmet.playerUnitSerialString

      // Check if this is a convertible helmet
      const isConvertibleHelmet = selectedHelmet.features?.toLowerCase() === 'convertible' && convertibleValidation?.isValid

      // Step 1: For convertible helmets, use PutPlayersTeamsEquipment instead of PostPlayerUnitToTeam
      if (isConvertibleHelmet && convertibleValidation?.requiredPlayer) {
        try {
          const equipmentAssignmentPayload = {
            teamID: selectedTargetTeam?.teamID || 0,
            playerID: convertibleValidation.requiredPlayer.playerID,
            firstName: convertibleValidation.requiredPlayer.firstName,
            lastName: convertibleValidation.requiredPlayer.lastName,
            jerseyNumber: convertibleValidation.requiredPlayer.jerseyNumber,
            positionID: convertibleValidation.requiredPlayer.positionID || 0,
            playerUnitID: selectedHelmet.playerUnitID || 0
          }

          await API.PutPlayersTeamsEquipment(equipmentAssignmentPayload)
          logger.info('Convertible helmet assigned via PutPlayersTeamsEquipment', { equipmentAssignmentPayload })
        } catch (error: any) {
          const apiDetail = extractApiErrorDetail(error)
          const equipmentError = 'PlayersTeamsEquipment assignment failed: ' + (apiDetail || error?.message || error)
          logger.error(equipmentError, { error, helmetInfo, responseData: error?.response?.data })
          addAlert('error', equipmentError)
          return
        }
      } else {
        // Step 1: For non-convertible helmets, transfer player unit to new team using PostPlayerUnitToTeam
        let transferError = ''
        try {
          const transferPayload = {
            playerUnitSerialString: selectedHelmet.playerUnitSerialString,
            playerUnitFirmwareVersion: selectedHelmet.playerUnitFirmwareVersionSemVer || '',
            playerUnitHardwareNumber: Number(selectedHelmet.playerUnitHardwareNumber),
            teamID: selectedTargetTeam ? selectedTargetTeam.teamID : 0,
          }
          
          // Validate transfer payload
          if (!transferPayload.playerUnitSerialString || !transferPayload.teamID || !Number.isFinite(transferPayload.playerUnitHardwareNumber)) {
            logger.error('Invalid transfer payload', { transferPayload })
            addAlert('error', 'Invalid transfer payload. Verify player unit details and target team selection.')
            return
          }
          
          await transferPlayerUnitToTeam(
            transferPayload.playerUnitSerialString,
            transferPayload.playerUnitFirmwareVersion,
            transferPayload.playerUnitHardwareNumber,
            transferPayload.teamID
          )
        } catch (error: any) {
          const apiDetail = extractApiErrorDetail(error)
          transferError = 'PlayerUnitToTeam transfer failed: ' + (apiDetail || error?.message || error)
          logger.error(transferError, { error, helmetInfo, responseData: error?.response?.data })
          addAlert('error', transferError)
          return
        }
      }

      // Step 2: Update helmet information with all extended details
      let putHelmetError = ''
      try {
        const helmetToUpdate = {
          rfidBarcode: selectedHelmet.rfidBarcode || '',
          playerUnitSerialString: selectedHelmet.playerUnitSerialString,
          helmetRFIDLabel: selectedHelmet.helmetRFIDLabel || '',
          originalMaterialNo: selectedHelmet.originalMaterialNo || '',
          helmetHatSize: selectedHelmet.helmetHatSize || null,
          features: selectedHelmet.features ?? '',
          shell: selectedHelmet.shell || '',
          shellColor: selectedHelmet.shellColor || '',
          colorCode: selectedHelmet.colorCode || '',
          paintCode1: selectedHelmet.paintCode1 || '',
          paintCode1Description: selectedHelmet.paintCode1Description || '',
          paintCode2: selectedHelmet.paintCode2 || '',
          paintCode2Description: selectedHelmet.paintCode2Description || '',
          helmetType: selectedHelmet.helmetType || '',
          helmetColor: selectedHelmet.helmetColor || '',
          paintDesignCode: selectedHelmet.paintDesignCode || '',
          paintDesignDescription: selectedHelmet.paintDesignDescription || '',
          helmet_config_id: selectedHelmet.helmet_config_id ?? null
        }
        
        // Validate helmetToUpdate
        if (!helmetToUpdate.playerUnitSerialString) {
          logger.error('Invalid helmetToUpdate payload', { helmetToUpdate })
          addAlert('error', 'Invalid helmetToUpdate payload')
          return
        }
        
        await API.PutHelmet(helmetToUpdate as any, selectedHelmet.playerUnitSerialString)
      } catch (error: any) {
        putHelmetError = 'Helmet update failed: ' + (error?.message || error)
        logger.error(putHelmetError, { error, helmetInfo })

        // Rollback: only apply to non-convertible helmets that used PostPlayerUnitToTeam
        if (!isConvertibleHelmet) {
          const sourceTeamID = selectedSourceTeam?.teamID
          if (sourceTeamID) {
            try {
              await transferPlayerUnitToTeam(
                selectedHelmet.playerUnitSerialString,
                selectedHelmet.playerUnitFirmwareVersionSemVer || '',
                Number(selectedHelmet.playerUnitHardwareNumber),
                sourceTeamID
              )
              logger.info('Rollback successful: player unit returned to original team', { sourceTeamID })
              addAlert('error',
                'Transfer rolled back — helmet metadata update failed. The player unit was returned to its original team.'
              )
            } catch (rollbackErr: any) {
              logger.error('Rollback failed: could not return player unit to original team', {
                error: rollbackErr,
                helmetInfo,
                sourceTeamID
              })
              addAlert('error',
                'CRITICAL: Transfer partially failed and rollback failed. Manual intervention required for player unit S/N: ' + selectedHelmet.playerUnitSerialString,
                30000 as any
              )
            }
          } else {
            addAlert('error',
              'Transfer partially failed — helmet update error. Please verify team assignment for player unit S/N: ' + selectedHelmet.playerUnitSerialString
            )
          }
        } else {
          // For convertible helmets, the PutPlayersTeamsEquipment was already called, so just show error
          addAlert('error',
            'Transfer partially failed — helmet metadata update error. The helmet was already assigned to the RIDDELL player but metadata could not be updated for player unit S/N: ' + selectedHelmet.playerUnitSerialString
          )
        }
        return
      }

      // Step 3: Success UI and cleanup
      const sourceDisplay = helmetInfo.sourceOrgName ? (selectedSourceTeam ? `${helmetInfo.sourceOrgName} - ${selectedSourceTeam.teamName}` : helmetInfo.sourceOrgName) : 'source organization'
      const targetDisplay = selectedTargetOrg ? (selectedTargetTeam ? `${selectedTargetOrg.orgName} - ${selectedTargetTeam.teamName}` : selectedTargetOrg.orgName) : 'target organization'
      const helmetDisplay = selectedHelmet.helmetRFIDLabel || selectedHelmet.rfidBarcode || 'helmet'
      
      // Store transfer success information for display
      setTransferSuccess({
        helmetDisplay,
        sourceDisplay,
        targetDisplay
      })
      
      // Scroll to top to show header after successful transfer
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
      addAlert('success', `Helmet ${helmetDisplay} successfully transferred from ${sourceDisplay} to ${targetDisplay}`)

      // Reset form after successful transfer
      setValue('sourceOrgID', '')
      setValue('sourceTeamID', '')
      setValue('helmetID', '')
      setValue('targetOrgID', '')
      setValue('targetTeamID', '')
      setValue('confirmTransfer', false)
      setSelectedSourceOrg(null)
      setSourceTeamHelmets([])
      setAvailableTargetTeams([])
      setSelectedTargetTeam(null)
      setSelectedHelmet(null)
      setHelmetInfo(null)
      setTransferSuccess(null)
      setConvertibleValidation(null)
    } catch (error: any) {
      logger.error('Helmet organization transfer failed', { module: 'MoveHelmetOrgFlow', data: { error: error.message } })
      handleApiError(error, 'Transfer failed')
    } finally {
      setIsSubmitting(false)
    }
  }
  // Helper function to validate that we have all required data for helmet transfer
  const validateTransferData = (helmet: OrgHelmet, targetOrg: Organization | null): boolean => {
    if (!helmet.playerUnitID) {
      addAlert('error', 'Missing helmet player unit information')
      return false
    }

    if (!targetOrg) {
      addAlert('error', 'Target organization not selected')
      return false
    }
    if (!selectedTargetTeam) {
      addAlert('error', 'Target team information not loaded')
      return false
    }
    
    return true
  }
  
  const handleReset = () => {
    // Reset form values
    setValue('sourceOrgID', '')
    setValue('sourceTeamID', '')
    setValue('helmetID', '')
    setValue('targetOrgID', '')
    setValue('targetTeamID', '')
    setValue('confirmTransfer', false)
    
    // Clear all state variables
    setSelectedSourceOrg(null)
    setSelectedSourceTeam(null)
    setSourceTeams([])
    setSourceTeamLoadError(null)
    setTargetTeamLoadError(null)
    setSourceTeamHelmets([])
    setAvailableTargetTeams([])
    setSelectedTargetTeam(null)
    setSelectedHelmet(null)
    setHelmetInfo(null)
    setTransferSuccess(null)
    
    // Reset loading states
    setIsSubmitting(false)
    setIsLoadingHelmets(false)
    setIsLoadingTargetTeam(false)
  }
  
  const availableTargetOrgs = useMemo(() => 
    organizations.filter(org => 
      org.orgName && org.orgName.trim() !== '' &&
      (selectedSourceOrg ? 
        // Allow source org if it has multiple teams, otherwise exclude it
        (sourceTeams.length > 1 || getOrgID(org) !== getOrgID(selectedSourceOrg)) 
        : true)
    ), [organizations, selectedSourceOrg, sourceTeams]
  )
  
  const helmetOptions = useMemo(() => 
    SourceTeamHelmets
      .map((helmet, index) => {
        const helmetID = helmet.rfidBarcode || `helmet-${index}`
        const displayLabel = helmet.helmetRFIDLabel || helmet.rfidBarcode || `Helmet ${index + 1}`
        const helmetDetails = `[${helmet.helmetID || 'N/A'}, ${helmet.rfidBarcode || 'N/A'}]`
        const playerInfo = helmet.playerName ? `${helmet.playerName} (#${helmet.jerseyNumber})` : 'Unassigned'
        return { helmetID, displayLabel, helmetDetails, playerInfo }
      }), [SourceTeamHelmets]
  )
  
  const selectedTargetOrg: Organization | null = useMemo(() => 
    watchedTargetOrgID ? 
      organizations.find(org => getOrgID(org) === Number(watchedTargetOrgID)) || null : null,
    [watchedTargetOrgID, organizations]
  )
    return (
      <>
        <div className="w-full max-w-7xl mx-auto px-2 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6 bg-neutral-50 min-h-screen">
        {/* Riddell Header */}
        <div className="bg-riddell-red text-white rounded-t-lg shadow-lg mb-3 sm:mb-4 lg:mb-6 w-full">
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
            <div className="flex flex-col sm:flex-row items-center sm:space-x-4 space-y-2 sm:space-y-0 w-full">
              <div className="bg-white p-1.5 sm:p-2 rounded flex-shrink-0">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-riddell-red rounded flex items-center justify-center">
                  <span className="text-white font-bold text-sm sm:text-lg">R</span>
                </div>
              </div>
              <div className="text-center sm:text-left flex-1 min-w-0">
                <h1 className="text-lg sm:text-2xl lg:text-3xl font-heading font-black tracking-tight">HELMET TRANSFER</h1>
                <p className="text-riddell-red-100 text-sm sm:text-base lg:text-lg font-medium">Organization Management System</p>
              </div>
              {/* Reset Button */}
              <ResetButton
                isVisible={!!(watchedSourceOrgID || selectedSourceOrg || selectedSourceTeam || helmetInfo || selectedTargetOrg || transferSuccess || sourceTeamLoadError)}
                onReset={handleReset}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl border border-neutral-200 w-full overflow-visible">
          <div className="p-3 sm:p-4 lg:p-6 w-full">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6 lg:space-y-8 w-full">
              {/* Source Organization Selection */}
              <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="source-org-heading">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">1</div>
                  <h2 id="source-org-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">SELECT SOURCE ORGANIZATION</h2>
                </div>
                <div className="w-full">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    {/* Source Organization */}
                    <div>
                      <label htmlFor="sourceOrgID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                        Source Organization
                      </label>
                      <SearchableDropdown
                        id="sourceOrgID"
                        name="sourceOrgID"
                        value={watchedSourceOrgID || ''}
                        onChange={(value) => {
                          setValue('sourceOrgID', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                          trigger('sourceOrgID') // Trigger validation
                          
                          // Call the same organization selection logic as the original select
                          if (value) {
                            const sourceOrgIDNum = parseInt(value, 10)
                            const selectedOrg = organizations.find(org => getOrgID(org) === sourceOrgIDNum)
                          
                          if (selectedOrg) {
                            handleOrganizationSelection(selectedOrg)
                              .then(result => {
                                setSelectedSourceOrg(selectedOrg)
                                setSourceTeams(result.teams)
                                setSourceTeamLoadError(null)
                                
                                if (!result.requiresTeamSelection && result.team) {
                                  // Auto-select the single team
                                  setSelectedSourceTeam(result.team)
                                  setValue('sourceTeamID', result.team.teamID.toString(), { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                                } else {
                                  // Multiple teams - user needs to select
                                  setSelectedSourceTeam(null)
                                  setValue('sourceTeamID', '', { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                                }
                              })
                              .catch(error => {
                                const msg = error instanceof Error ? error.message : String(error)
                                const isNoTeams = typeof msg === 'string' && msg.toLowerCase().includes('no teams found for organization')
                                if (isNoTeams) {
                                  setSourceTeamLoadError('No team for selected organization')
                                  setSelectedSourceOrg(null)
                                  setSourceTeams([])
                                  setSelectedSourceTeam(null)
                                  logger.info('No teams found for selected source organization', { module: 'MoveHelmetOrgFlow', data: { orgID: selectedOrg.orgID } })
                                } else {
                                  logger.error('Failed to load teams for organization', { module: 'MoveHelmetOrgFlow', data: { error: (error as Error).message } })
                                  addAlert('error', 'Failed to load teams for organization')
                                  setSelectedSourceOrg(null)
                                  setSourceTeams([])
                                  setSelectedSourceTeam(null)
                                }
                              })
                          } else {
                            setSelectedSourceOrg(null)
                            setSourceTeams([])
                            setSelectedSourceTeam(null)
                          }
                        } else {
                          setSelectedSourceOrg(null)
                          setSourceTeams([])
                          setSelectedSourceTeam(null)
                        }
                        }}
                        options={organizations
                          .filter(org => org.orgName && org.orgName.trim() !== '')
                          .map(org => ({
                            value: String(getOrgID(org)),
                            label: org.orgName
                          }))}
                        placeholder="Select source organization..."
                        disabled={isLoadingOrgs || isLoadingHelmets}
                        error={errors.sourceOrgID?.message}
                        required={true}
                        aria-busy={isLoadingOrgs}
                      />
                        {isLoadingOrgs && (
                        <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                          <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                          <span className="text-sm font-medium">Loading organizations...</span>
                        </div>
                      )}
                        {sourceTeamLoadError && (
                          <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                            <span className="mr-1">⚠</span>
                            No team under organization. Select different organization
                          </p>
                        )}
                    </div>
                    {/* Source Team Dropdown or Disabled Field */}
                    {selectedSourceOrg && sourceTeams.length > 1 && (
                      <div>
                        <label htmlFor="sourceTeamID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                          Source Team
                        </label>
                        <SearchableDropdown
                          id="sourceTeamID"
                          name="sourceTeamID"
                          placeholder="Select source team..."
                          options={sourceTeams.map(team => ({ 
                            value: team.teamID.toString(), 
                            label: team.teamName 
                          }))}
                          value={watch('sourceTeamID') || ''}
                          onChange={(value) => {
                            register('sourceTeamID', { required: 'Source team is required when multiple teams exist' })
                            setValue('sourceTeamID', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                          }}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-white border-2 border-gray-300 rounded-md focus:border-riddell-red focus:outline-none transition-colors duration-150"
                        />
                        {errors.sourceTeamID && (
                          <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors.sourceTeamID.message || 'Source team selection is required'}
                        </p>
                        )}
                      </div>
                    )}
                    {selectedSourceOrg && sourceTeams.length === 1 && (
                      <div>
                        <label className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                          Source Team (Auto-selected)
                        </label>
                        <div className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-gray-100 border-2 border-gray-300 rounded-md">
                          {sourceTeams[0]?.teamName || 'Loading...'}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Team Information Display */}
                  {selectedSourceOrg && selectedSourceTeam && (
                    <div className="mt-3 p-3 bg-success/10 border border-success/20 rounded-md">
                      <div className="flex items-center space-x-2 text-success">
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium">
                          Selected: {createOrgTeamDisplayName(selectedSourceOrg, selectedSourceTeam)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Helmet Selection - show when a source org+team is chosen; display message if no helmets */}
              {selectedSourceOrg && selectedSourceTeam && (
                <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="helmet-selection-heading">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                    <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">2</div>
                    <h2 id="helmet-selection-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">SELECT HELMET</h2>
                  </div>
                  <div className="w-full">
                    <label htmlFor="helmetID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                      <span className="block sm:inline">Helmet from</span> <span className="block sm:inline font-medium">{selectedSourceOrg.orgName}</span>
                    </label>
                    {isLoadingHelmets ? (
                      <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                        <span className="text-sm font-medium">Loading helmets...</span>
                      </div>
                    ) : SourceTeamHelmets.length > 0 ? (
                      <SearchableDropdown
                        id="helmetID"
                        name="helmetID"
                        placeholder="Select helmet..."
                        options={helmetOptions.map(option => ({ 
                          value: option.helmetID, 
                          label: `${option.displayLabel} - ${option.playerInfo}` 
                        }))}
                        value={watch('helmetID') || ''}
                        onChange={(value) => {
                          register('helmetID', { required: 'Helmet selection is required' })
                          setValue('helmetID', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                        }}
                        disabled={isLoadingHelmets}
                        aria-busy={isLoadingHelmets}
                      />
                    ) : (
                      <div className="mt-3 p-3 bg-warning/10 border border-warning/20 rounded-md text-warning text-sm font-medium" role="status">
                        No helmets to move for this team.
                      </div>
                    )}
                    {errors.helmetID && (
                      <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.helmetID.message || 'Helmet selection is required'}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {/* Helmet Information Display */}
              {helmetInfo && (
                <div className="bg-success/10 border-l-4 border-success p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="helmet-info-heading">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                    <div className="bg-success text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">3</div>
                    <h3 id="helmet-info-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">HELMET INFORMATION</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3 text-xs sm:text-sm w-full">
                    <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Source Org</span>
                      <span className="text-sm sm:text-base font-medium text-riddell-black break-words leading-tight">{helmetInfo.sourceOrgName}</span>
                    </div>
                    <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">RFID Label</span>
                      <span className="text-sm sm:text-base font-bold text-riddell-red">{helmetInfo.helmetRFIDLabel || 'N/A'}</span>
                    </div>
                    <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">RFID Barcode</span>
                      <span className="text-sm sm:text-base font-medium text-riddell-black break-all leading-tight">{helmetInfo.rfidBarcode || 'N/A'}</span>
                    </div>
                    <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Serial</span>
                      <span className="text-sm sm:text-base font-medium text-riddell-black break-all leading-tight">{helmetInfo.playerUnitSerialString}</span>
                    </div>
                    {helmetInfo.currentPlayer && (
                      <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                        <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Player</span>
                        <span className="text-sm sm:text-base font-medium text-riddell-black break-words leading-tight">
                          {helmetInfo.currentPlayer.playerName} (#{helmetInfo.currentPlayer.playerNumber})
                        </span>
                      </div>
                    )}
                  </div>
                  {helmetInfo.features?.toLowerCase() === 'convertible' && (
                    <div className="mt-4 p-3 sm:p-4 bg-warning/10 border border-warning/30 rounded-lg" role="alert">
                      <p className="text-sm sm:text-base font-bold text-warning uppercase tracking-wide mb-1">Convertible Helmet</p>
                      <p className="text-sm text-riddell-black">This is a Convertible helmet. The target organization must have a matching "RIDDELL {helmetInfo.helmetHatSize}" player to receive this helmet.</p>
                    </div>
                  )}
                </div>
              )}
              {/* Target Organization Selection */}
              {helmetInfo && (
                <div className="bg-neutral-50 border-l-4 border-riddell-red p-4 sm:p-6 rounded-lg w-full" role="group" aria-labelledby="target-org-heading">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-4">
                    <div className="bg-riddell-red text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm sm:text-base">4</div>
                    <h2 id="target-org-heading" className="text-lg sm:text-xl font-heading font-bold text-riddell-black">SELECT TARGET ORGANIZATION & TEAM</h2>
                  </div>
                  <div className="w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      {/* Target Organization */}
                      <div>
                        <label htmlFor="targetOrgID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                          Target Organization
                        </label>
                        <SearchableDropdown
                          id="targetOrgID"
                          name="targetOrgID"
                          value={watchedTargetOrgID || ''}
                          onChange={(value) => {
                            setValue('targetOrgID', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                            trigger('targetOrgID') // Trigger validation
                          }}
                          options={availableTargetOrgs
                            .filter(org => org.orgName && org.orgName.trim() !== '')
                            .map(org => ({
                              value: String(getOrgID(org)),
                              label: org.orgName
                            }))}
                          placeholder="Select target organization..."
                          disabled={isLoadingOrgs}
                          error={errors.targetOrgID?.message}
                          required={true}
                        />
                          {targetTeamLoadError && (
                            <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                              <span className="mr-1">⚠</span>
                              {targetTeamLoadError}
                            </p>
                          )}
                        {isLoadingTargetTeam && (
                          <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                            <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                            <span className="text-sm font-medium">Loading teams...</span>
                          </div>
                        )}
                      </div>
                      {/* Target Team Dropdown or Disabled Field */}
                      {selectedTargetTeam && availableTargetTeams.length === 1 && !isLoadingTargetTeam && (
                        <div>
                          <label className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                            Target Team (Auto-selected)
                          </label>
                          <div className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-gray-100 border-2 border-gray-300 rounded-md">
                            {selectedTargetTeam.teamName || selectedTargetTeam.name || 'Loading...'}
                          </div>
                        </div>
                      )}
                      {availableTargetTeams.length > 1 && !isLoadingTargetTeam && (
                        <div>
                          <label htmlFor="targetTeamID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                            Target Team
                          </label>
                          <SearchableDropdown
                            id="targetTeamID"
                            name="targetTeamID"
                            placeholder="Select target team..."
                            options={availableTargetTeams.map(team => ({ 
                              value: team.teamID.toString(), 
                              label: team.teamName || team.name || `Team ${team.teamID}` 
                            }))}
                            value={watch('targetTeamID') || ''}
                            onChange={(value) => {
                              register('targetTeamID', { required: 'Target team is required' })
                              setValue('targetTeamID', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                            }}
                            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-white border-2 border-gray-300 rounded-md focus:border-riddell-red focus:outline-none transition-colors duration-150"
                          />
                          {errors.targetTeamID && (
                            <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              {errors.targetTeamID.message || 'Target team selection is required'}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Confirmation and Submit */}
              {helmetInfo && selectedTargetTeam && convertibleValidation?.isValid !== false && (
                <div className="bg-riddell-red/10 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="confirm-transfer-heading">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4 lg:mb-6">
                    <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">{availableTargetTeams.length > 1 ? '6' : '5'}</div>
                    <h2 id="confirm-transfer-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">CONFIRM TRANSFER</h2>
                  </div>
                  <div className="mb-3 sm:mb-4 lg:mb-6 w-full">
                    <label className="flex items-start space-x-2 sm:space-x-3 cursor-pointer">
                      <input
                        {...register('confirmTransfer', { required: 'You must confirm the transfer' })}
                        type="checkbox"
                        className="mt-0.5 sm:mt-1 h-4 w-4 sm:h-5 sm:w-5 text-riddell-red bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-riddell-red focus:ring-offset-0 transition-all duration-150 flex-shrink-0"
                      />
                      <span className="text-xs sm:text-sm lg:text-base text-riddell-black leading-relaxed">
                        I confirm that I want to transfer helmet <strong className="text-riddell-red">{helmetInfo.helmetRFIDLabel}</strong> from{' '}
                        <strong className="text-riddell-black break-words">{helmetInfo.sourceOrgName}</strong>
                        {selectedSourceTeam && (
                          <span>, team <strong className="text-riddell-red">{selectedSourceTeam.teamName}</strong></span>
                        )} to{' '}
                        <strong className="text-riddell-black break-words">{selectedTargetOrg ? selectedTargetOrg.orgName : 'the selected target organization'}</strong>
                        {selectedTargetTeam && (
                          <span>, team <strong className="text-riddell-red">{selectedTargetTeam.teamName || selectedTargetTeam.name}</strong></span>
                        )}.
                      </span>
                    </label>
                    {errors.confirmTransfer && (
                      <p className="mt-2 sm:mt-3 text-sm text-error font-medium flex items-center" role="alert">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.confirmTransfer.message || 'Transfer confirmation is required'}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting || isValidatingConvertible || (convertibleValidation !== null && !convertibleValidation.isValid)}
                    className="w-full py-3 sm:py-4 text-base sm:text-lg font-bold uppercase tracking-wide bg-riddell-red text-white border-2 border-riddell-red rounded-md hover:bg-riddell-red-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
                        <span>TRANSFERRING...</span>
                      </div>
                    ) : (
                      'TRANSFER HELMET'
                    )}
                  </Button>
                </div>
              )}
              {/* Transfer Success Display */}
              {transferSuccess && (
                <div className="bg-success/10 border-l-4 border-success p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="transfer-success-heading">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                    <div className="bg-success text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 id="transfer-success-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-success">TRANSFER COMPLETED SUCCESSFULLY</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm w-full">
                    <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Helmet</span>
                      <span className="text-sm sm:text-base font-bold text-riddell-red">{transferSuccess.helmetDisplay}</span>
                    </div>
                    <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">From</span>
                      <span className="text-sm sm:text-base font-medium text-riddell-black break-words leading-tight">{transferSuccess.sourceDisplay}</span>
                    </div>
                    <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">To</span>
                      <span className="text-sm sm:text-base font-medium text-riddell-black break-words leading-tight">{transferSuccess.targetDisplay}</span>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Convertible Helmet Validation Modal */}
      {convertibleValidation && (
        <ConfirmationDialog
          isOpen={showConvertibleModal}
          title={convertibleValidation.isValid ? 'Convertible Helmet Validated' : 'Convertible Helmet Requirement Not Met'}
          message={
            convertibleValidation.isValid && convertibleValidation.requiredPlayer
              ? `This helmet will be assigned to player ${convertibleValidation.requiredPlayer.playerName} (Jersey #${convertibleValidation.requiredPlayer.jerseyNumber}).`
              : convertibleValidation.errorMessage || 'Unable to validate convertible helmet.'
          }
          confirmText="OK"
          variant={convertibleValidation.isValid ? 'info' : 'warning'}
          onConfirm={() => setShowConvertibleModal(false)}
          onCancel={() => setShowConvertibleModal(false)}
        />
      )}
    </>
    )
}

export default MoveHelmetOrgFlow